//  Summer Meals Map
//	web services - Aaron Zygmunt: azygmunt2@cps.edu
//  06/2025
//  04/2026 - global ui updates

// Naming conventions used in this code:
// variables that begin with $ contain jquery elements or selectors
// functions that begin with $ return a single jquery element

import {setOptions, importLibrary} from "@googlemaps/js-api-loader";
import $ from "jquery";
import "jquery-ui/dist/jquery-ui.min";
import DataTable from "datatables.net-dt";
import markerSvg from "../../../global/images/marker_pin.svg";
import translations from "../../../global/translations.json";
import {
    $addressDirectionLink,
    $infoRow,
    $resultTable,
    $targets,
    animateMarker,
    chicago,
    chicagoBounds,
    color,
    drawSearchRadiusCircle,
    geocodeRequest,
    getAPIValues,
    googleAPIKey,
    initModal,
    inputSubstringMatch,
    menuHighlight,
    metersPerMile,
    parseTranslations,
    positionMarkersOnMap,
    scrollToRow,
    setAddressMarker,
    setMapBounds,
    stopAnimateMarker,
    toSnakeCase,
    translatePage,
    updateCMSElements
} from "../../../global/map-library";
import "../../../global/map-library.scss";
import "../scss/styles.scss";

// Get parameters from URL and default to "en" if not passed
const urlParams = new URLSearchParams(window.location.search);
let lang = "en";
if (urlParams.get("language")) {
    lang = urlParams.get("language").toLowerCase();
} else if (urlParams.get("lang")) {
    lang = urlParams.get("lang").toLowerCase();
}

const fullscreen = urlParams.get("fullscreen");

// add selection highlighting to jquery ui autocomplete menu
$.ui.autocomplete.prototype._renderItem = menuHighlight;

setOptions({
    key: googleAPIKey("AIzaSyCc12vnyiq8rQWNtZbnZ836F_SXzAaqOL0"), // Meal Distribution api key - should be unique for every project
    v: "weekly",
});

try {
    ///////////////////////////////////// GOOGLE MAP LIBRARIES /////////////////////////////////////

    const {Map, InfoWindow} = await importLibrary("maps");
    const {AdvancedMarkerElement, PinElement} = await importLibrary("marker");
    const {AutocompleteSessionToken, AutocompleteSuggestion} = await importLibrary("places");

    ///////////////////////////////////// GLOBALS /////////////////////////////////////

    const mealAPI = "https://api.cps.edu/maps/cps/GeoJSON";
    const mealAPIParameters = {"mapname": "MEAL", "year": "current"};

    const markers = []; // hold the pins currently displayed on the map
    let filteredData;  // global array to hold the data to be displayed. it will be filled with api data, reduced by filtering, reset to the original dataset,
    // or be refilled with radius reduced api data throughout the running of this script
    const infoWindow = new InfoWindow();
    let addressMarker;
    let loaded = false; // used to determine initial map load and prevent looping
    let dataTable; // used for datatables.js initialization
    let location // holds the google map location after an address search 
    let radiusData // holds the api data after an address search 
    let searchRadiusCircle; // holds the google map circle object after an address search

    /*************************************************************************************************************/

    // get values from the school api and meal spreadsheet
    const mealData = await getAPIValues(mealAPI, mealAPIParameters);

    const dataAll = [...mealData.features];
    dataAll.sort((a, b) => a.properties.Name.localeCompare(b.properties.Name));

    // Update the page with contents from the CMS
    updateCMSElements($("h1", $targets.overlay), window.appConfig.pageHeading, $($targets.bodyText), window.appConfig.pageContent);

    // gather the translations from the page
    const pageTranslations = parseTranslations($targets.overlay);

    // append page translations to translations object
    Object.keys(pageTranslations).forEach(l => {
        Object.keys(pageTranslations[l]).forEach(t => {
            translations[l][t] = pageTranslations[l][t];
        });
    });

    translatePage(lang, $targets);

    // create main map object
    const map = new Map($targets.map[0], {
        mapId: "ab574e903403777f3ad7f58f", // Meal Distribution map id - should be unique for every project
        center: chicago,
        streetViewControl: false,
        fullscreenControl: false,
        mapTypeControl: true,
        mapTypeControlOptions: {
            style: google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
            position: google.maps.ControlPosition.TOP_RIGHT
        }
    });

    updateFilters(dataAll);

    // show UI
    if (fullscreen) {
        $targets.mainWrapper.toggle(false);
        $targets.displayToggle.attr("aria-expanded", (index, attr) => (attr === "true" ? "false" : "true"));
    }
    $targets.dataLoader.remove();
    $targets.overlay.removeAttr("hidden");

    // Create a session token for autocomplete suggestions
    const token = new AutocompleteSessionToken();

    // initialize the autocomplete menu
    $targets.input.autocomplete({
        // use google autocomplete as a dynamic data source
        source: async function (request, response) {
            // create the list of addresses with google's Places(new) autocomplete with a shortened address as the label and "address" as the value
            let addressList = [];
            if (request.term.length > 1) {
                const autocompleteRequest = {
                    input: request.term,
                    locationRestriction: chicagoBounds,
                    origin: chicago,
                    includedPrimaryTypes: ["street_address"],
                    language: "en-US",
                    region: "us",
                    sessionToken: token
                };
                const {suggestions} = await AutocompleteSuggestion.fetchAutocompleteSuggestions(autocompleteRequest);
                for (const suggestion of suggestions) {
                    // only add addresses that end with "Chicago, IL"
                    if (suggestion.placePrediction.text.toString().endsWith("Chicago, IL")) {
                        addressList.push({
                            label: suggestion.placePrediction.mainText.toString(),
                            value: "address"
                        });
                    }
                }
            }

            // create the schools list in label/value format
            const schoolList = filteredData.filter((s) => s.properties.Name.toUpperCase().includes(request.term.toUpperCase())).map(s => ({
                label: s.properties.Name,
                value: "school"
            }));

            // return the list for the autocomplete menu as the response
            response([...schoolList, ...addressList]);
        },
        focus: function (event, ui) { // autocomplete result is focused on
            $targets.input.val(ui.item.label);
            return false;
        },
        select: (e, ui) => {
            e.preventDefault();
            $targets.input.val(ui.item.label);
            $targets.input.autocomplete("close");
            if (ui.item.value === "address") {
                geocodeRequest(ui.item.label, googleGeoCallback);
            } else {
                // this is a school. Select the school with a string match on its label
                selectSchool(markers.find(m => m.properties.Name === ui.item.label));
            }
        }
    });

    /********************* EVENTS *********************/

    // pan the map into the proper position after everything is drawn
    map.addListener("tilesloaded", () => {
        if (!loaded) {
            setTimeout(() => {
                // fires more reliably when wrapped in an asynchronous call. Using a delay 0 in this case
                positionMarkersOnMap(map, $targets);
                loaded = true;
            }, 0);
        }
    });

    // remove the table row highlight and clear the input field when the infowindow is closed
    infoWindow.addListener("close", () => {
        $targets.input.val("");
        $("tr.selected-row").removeClass("selected-row");
    });

    // mixed search button clicked
    $targets.search.on("click", () => {
        mixedSearch($targets.input.val());
    });

    // input field enter key
    $targets.input.on("keydown", async (e) => {
        if (e.key === "Enter") { // Check if the Enter key is pressed
            if ($targets.input.autocomplete) {
                $targets.input.autocomplete("close");
            }
            mixedSearch($targets.input.val());
        }
    });

    // FILTER EVENT LOGIC:
    // Set the UI state first, then filter the data by examining the UI and applying the filters
    // This way, the data shown and current state of the UI should always be in sync
    $("button", $targets.filters).on("click", function () {
        if ($(this).hasClass("active")) {
            $(this).removeClass("active");
        } else {
            $(".active", $targets.filters).removeClass("active");
            $(this).addClass("active");
        }
        // if there is radiusData from a radius search, then filter the radiusData. otherwise filter dataAll
        updateFilters(radiusData ? radiusData : dataAll);
    });

    // radius menu change
    $targets.radius.on("change", (e) => {
        geocodeRequest(location, googleGeoCallback);
    });

    // translation button - toggle language
    $targets.translate.on("click", (e) => {
        lang = lang === "en" ? "es" : "en";
        infoWindow.close();
        translatePage(lang, $targets);
        updateResults();
    });

    // find me button - do a geocode request with the user's browser location
    $targets.findMe.on("click", (e) => {
        //check to see if http or https (only works with https)
        if (top.location.href.split(":")[0] !== "https") {
            const $modal = initModal("Secure Connection Required", $("<p>").html("This feature requires a secure connection."));
            const myModal = new Modal($modal);
            myModal.show();
            return false;
        }

        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(position => {
                geocodeRequest(new google.maps.LatLng(position.coords.latitude, position.coords.longitude), googleGeoCallback);
            }, err => {
                console.warn(`ERROR(${err.code}): ${err.message}`);
                const $modal = initModal("Browser Location Required", $("<p>").html("This feature requires location to be enabled in your browser and OS."));
                const myModal = new Modal($modal);
                myModal.show();
                return false;
            }, {timeout: 4000});
        }
    });

    // reset button
    $targets.reset.on("click", resetMap);

    // collapse main window
    $targets.displayToggle.on("click", (e) => {
        $targets.mainWrapper.toggle({
            duration: 0,
            complete: () => {
                $targets.displayToggle.attr("aria-expanded", (index, attr) => (attr === "true" ? "false" : "true"));

                scrollToRow($("tr.selected-row"), $targets.results);

                // center the map on the infoWindow if there is one, otherwise use the data to set boundaries
                if (infoWindow.getMap()) {
                    map.setCenter(infoWindow.getPosition());
                } else {
                    setMapBounds(map, markers, addressMarker, searchRadiusCircle);
                }
                positionMarkersOnMap(map, $targets);
            }
        });
    });

    // resize the results list and move the markers if the window size changes
    $(window).on("resize", () => {
        setMapBounds(map, markers, addressMarker, searchRadiusCircle);
        positionMarkersOnMap(map, $targets);
    });

    //hold mouse down for 1.5 seconds to trigger radius search
    new LongPress(1500);
    map.addListener("longPress", event => geocodeRequest(event.latLng, googleGeoCallback));

    /********************* UTILITY FUNCTIONS *********************/

    // read the current UI state, apply filters to the data, then update the results display
    function updateFilters(data) {
        filteredData = filterData(data);
        setMarkers(filteredData);
        updateResults();

        function filterData(data) {
            const filters = getFilterState();
            adjustFilterUI(data);
            return applyFilters(data, filters);

            // update the filters using the current state of the UI
            function getFilterState() {
                const f = {type: null};
                const activeFilter = $(".active", $targets.filters).attr("id");
                if (activeFilter === "filter-lunchStop") {
                    f.type = "LunchStop";
                } else if (activeFilter === "filter-inSchool") {
                    f.type = "inSchool";
                }
                return f;
            }

            // enable/disable filters based on incoming data
            function adjustFilterUI(data) {
                // Disable the filters if there are no elementary or high schools in the current dataset
                const foundLunchStop = data.find(f => f.properties["Meal Service"] === "LunchStop");
                const foundInschool = data.find(f => f.properties["Meal Service"] !== "LunchStop");
                if (!foundLunchStop || !foundInschool) {
                    $(".filter-button", $targets.filters).attr("hidden", "hidden");
                } else {
                    $(".filter-button", $targets.filters).removeAttr("hidden");
                }
            }

            // filter the dataset from data based on the filters array
            function applyFilters(data) {
                let filtered = [...data]; // start with the full dataset
                if (filters.type === "LunchStop") {
                    filtered = data.filter(f => f.properties["Meal Service"] === "LunchStop");
                } else if (filters.type === "inSchool") {
                    filtered = data.filter(f => f.properties["Meal Service"] !== "LunchStop");
                }
                return filtered;
            }
        }
    }

    // set the array of map markers based on an incoming set of features
    // this array becomes the master source of data for the script
    // each marker contains an advancedMarker element that references the google advancedMarker object that draws the pin
    // and a program field that contains all the data for the program
    function setMarkers(data) {
        // clear out any existing markers
        markers.forEach(m => m.map = null);
        markers.length = 0;

        data.forEach(f => {
            const pinStyle = schoolStyle(f.properties);
            pinStyle.scale = .7;
            const advancedMarker = new AdvancedMarkerElement({
                map: map,
                position: {
                    lng: f.geometry.coordinates[0],
                    lat: f.geometry.coordinates[1]
                },
                title: f.properties.Name,
            });
            advancedMarker.append(new PinElement(pinStyle));

            // add event listener for the marker information popup
            advancedMarker.addListener("gmp-click", () => {
                // open the info window for a given marker
                openInfoWindow(advancedMarker);

                // set the highlight and scroll to the selected row
                $(`.resultRow_${toSnakeCase(f.properties.Name)}`).addClass("selected-row");
                scrollToRow($("tr.selected-row"), $targets.results);

                // set the input field to match the selection
                $targets.input.val(advancedMarker.properties.Name);
            });

            advancedMarker.properties = f.properties;

            // add this marker to the array of all markers
            markers.push(advancedMarker);

            // return a pin color based on type and the current tab
            function schoolStyle(p) {
                if (p["Lunch Stop Start Time"]) {
                    return {
                        background: color.orange,
                        borderColor: color.darkOrange,
                        glyphText: ""
                    };
                } else {
                    return {
                        background: color.blue,
                        borderColor: color.darkBlue,
                        glyphText: ""
                    };
                }
            }
        });
    }

    function updateResults() {
        infoWindow.close();
        setMapBounds(map, markers, addressMarker, searchRadiusCircle);
        positionMarkersOnMap(map, $targets);

        // put the results table into the active tab
        $targets.results.html($resultTable($tableHead(), $tableBody(markers)));

        if (dataTable) {
            dataTable.destroy();
        }
        dataTable = new DataTable(".resultList", {
            paging: false,
            searching: false,
            info: false,
            order: [[0, "asc"]],
            drawCallback: function (settings) {
                setMapBounds(map, markers, addressMarker, searchRadiusCircle);
                positionMarkersOnMap(map, $targets);
            }
        });
        $(".dt-info").html("");

        // return the results table header
        function $tableHead() {
            // create the table header row
            const $tr = $("<tr>").addClass("results-header");
            const $nameCell = $("<th>").html(`${translations[lang]["label-schools"]}: ${markers.length}`);
            // const $mealCell = $("<th>").html(translations[lang]["label-meal-service"]);
            const $breakfastCell = $("<th>").html(translations[lang]["label-breakfast"]);
            const $lunchCell = $("<th>").html(translations[lang]["label-lunch"]);
            const $daysCell = $("<th>").html(translations[lang]["label-days-of-week"]);
            const $datesCell = $("<th>").html(translations[lang]["label-service-dates"]);
            const $directionsCell = $("<th>").html(translations[lang]["label-address"]);

            return $("<thead>").html($tr.append($nameCell, $breakfastCell, $lunchCell, $daysCell, $datesCell, $directionsCell));
        }

        // return results table body rows
        function $tableBody(markers) {
            const $out = $("<tbody>");

            // make a row for each marker
            markers.forEach(m => {
                $out.append($resultRow(m));
            });

            return ($out);

            function $resultRow(marker) {
                const p = marker.properties;
                const type = p["Meal Service"] === "LunchStop" ? "lunchStop" : "inSchool";

                // CREATE ROW
                const $row = $("<tr>").addClass(`resultRow_${toSnakeCase(p.Name)} type_${type}`);

                // CREATE CELLS
                const $schoolLink = $("<button>").addClass("d-flex btn btn-link w-100 text-start");
                $schoolLink.html($("<div>").addClass("marker-icon").html(markerSvg));
                $schoolLink.append($("<div>").html(p.Name));
                const $cellName = $("<td>").addClass("bcq-p-1 bcq-ps-3").html($schoolLink);
                // const $cellService = $("<td>").addClass("bcq-p-1").html(p["Meal Service"]);
                const now = new Date();
                const today = `${now.getMonth() + 1}/${now.getDate()}/${now.getFullYear()}`;
                const $cellBreakfast = $("<td>").addClass("bcq-p-1").html(p["Breakfast Start Time"] !== "0" ? `${p["Breakfast Start Time"]} - ${p["Breakfast End Time"]}` : "");
                const breakfastSort = new Date(`${today} ${p["Breakfast Start Time"]}`).getTime();
                $cellBreakfast.attr("data-order", breakfastSort);
                const $cellLunch = $("<td>").addClass("bcq-p-1").html(p["Lunch Start Time"] !== "0" ? `${p["Lunch Start Time"]} - ${p["Lunch End Time"]}` : "");
                const lunchSort = new Date(`${today} ${p["Lunch Start Time"]}`).getTime();
                $cellLunch.attr("data-order", lunchSort);
                const $cellDays = $("<td>").addClass("bcq-p-1").html(p["Days Of the Week"]);
                const startDateSort = new Date(p["Start Date"]).getTime();
                const $cellDates = $("<td>").addClass("bcq-p-1").html(`${p["Start Date"]} - ${p["End Date"]}`).attr("data-order", startDateSort);
                const $addressLink = $addressDirectionLink(p.Address, addressMarker);
                const $cellDirection = $("<td>").addClass("bcq-p-1").html($addressLink);

                // POPULATE ROW
                $row.append($cellName, $cellBreakfast, $cellLunch, $cellDays, $cellDates, $cellDirection);

                // ADD EVENTS
                $schoolLink.on("click", () => {
                    openInfoWindow(marker);
                    $targets.input.val(marker.properties.Name);
                    $row.addClass("selected-row");
                });
                $row.on("mouseenter", () => animateMarker(marker));
                $row.on("mouseleave", () => stopAnimateMarker(marker));

                return $row;
            }
        }
    }

    // populates the info window
    function openInfoWindow(marker) {
        const position = marker.position;

        // have the info window appear in the center of the circles and offset higher on icons.
        infoWindow.setOptions({
            pixelOffset: new google.maps.Size(0, -26),
            maxWidth: 600,
        });
        infoWindow.setHeaderContent(infoWindowHeaderContent(marker.properties));
        infoWindow.setContent(infoWindowContent(marker.properties));
        infoWindow.setPosition(position);
        infoWindow.open(map);
        // remove the selection highlight from the "X" in the newly opened window
        infoWindow.addListener("visible", () => {
            $(".gm-style-iw-chr button[title=Close]").trigger("blur");
        });

        map.setCenter(position);
        map.setZoom(15);
        positionMarkersOnMap(map, $targets);

        function infoWindowHeaderContent(p) {
            // start building the detail div
            const $content = $("<div>").addClass("infowindow-content");
            // school name as header
            $content.html($("<h4>").addClass("mb-1").html(p["Name"]));
            return $content[0];
        }

        // create the <div> with information that goes in the marker popup
        function infoWindowContent(p) {
            // start building the detail div
            const $content = $("<div>").addClass("infowindow-content");

            const $table = $("<table>").addClass("table table-sm table-striped mb-0");
            const $tbody = $("<tbody>");
            // add the detail rows

            const $addressLink = $addressDirectionLink(p["Address"], addressMarker);
            $tbody.append($infoRow(`${translations[lang]["label-address"]}: `, $addressLink).addClass("mb-1"));
            const $phoneLink = $("<a>").attr({href: `tel:${p["Phone"].replace(/\D/g, "")}`}).html(p["Phone"]);

            $tbody.append($infoRow(`${translations[lang]["label-phone"]}: `, $phoneLink).addClass("mb-1"));
            $tbody.append($infoRow(`${translations[lang]["label-service-dates"]}: `, `${p["Start Date"]} - ${p["End Date"]}`).addClass("mb-1"));
            $tbody.append($infoRow(`${translations[lang]["label-meal-service"]}: `, p["Meal Service"]).addClass("mb-1"));

            if (p["Breakfast Start Time"] && p["Breakfast Start Time"] !== "0") {
                $tbody.append($infoRow(`${translations[lang]["label-breakfast"]}: `,
                    `${p["Breakfast Start Time"]} - ${p["Breakfast End Time"]}`).addClass("mb-1"));
            }

            if (p["Lunch Start Time"] && p["Lunch Start Time"] !== "0") {
                $tbody.append($infoRow(`${translations[lang]["label-lunch"]}: `,
                    `${p["Lunch Start Time"]} - ${p["Lunch End Time"]}`).addClass("mb-1"));
            }

            if (p["Lunch Stop Time"]) {
                $tbody.append($infoRow(`${translations[lang]["label-lunchstop"]}: `,
                    `${p["Lunch Stop Start Time"]} - ${p["Lunch Stop End Time"]}`).addClass("mb-1"));
            }

            $content.html($table.html($tbody));
            return $content[0];
        }
    }

    // perform a search on the markers from the general input field - does 
    function mixedSearch(input) {
        $targets.input.trigger("blur");

        if (input) {
            const properties = filteredData.map(d => d.properties);
            const match = inputSubstringMatch(input, properties, "Name");
            if (match) {
                $targets.input.val(match.Name);
                selectSchool(markers.find(m => m.properties.Name === match.Name));
            } else {
                geocodeRequest($targets.input.val(), googleGeoCallback);
            }
        } else {
            const myModal = new Modal(initModal("Enter Address or School", $("<p>").html(`Please enter an address or school`)));
            myModal.show();
        }
    }

    // open an infowindow for a marker and scroll to its row in the table
    function selectSchool(m) {
        openInfoWindow(m);
        $(`.resultRow_${toSnakeCase(m.properties.Name)}`).addClass("selected-row");
        scrollToRow($("tr.selected-row"), $targets.results);
        $targets.input.val(m.properties.Name);
        $targets.input.trigger("blur");
    }

    // reset all map elements to their initial states
    function resetMap() {
        clearMapElements(); //clear markers

        // reset search
        $targets.input.val("");
        location = null;

        // remove the quicksort
        if (dataTable) {
            dataTable.destroy();
        }

        // reset the filter buttons
        $(".active", $targets.filters).removeClass("active");

        // reset the search radius
        $targets.radius.val("2");
        $targets.radius.attr({"hidden": "hidden"});

        // reset the filteredData array to the full dataset using updateFilters(dataAll) to reset filter UI state
        updateFilters(dataAll);

        // reset any address search
        radiusData = null;

        // reposition map
        setMapBounds(map, markers, addressMarker);
        positionMarkersOnMap(map, $targets);
    }

    /******************** MAP FUNCTIONS ********************/

    // take the returned address, format it, put it in the address field, do a geojson search of cps data with a radius restriction, and display the results
    function googleGeoCallback(results, status, request) {
        $targets.input.trigger("blur");

        // reset any existing filters - prevents case where all data could be filtered out
        $(".active", $targets.filters).removeClass("active");

        // geocoder status ok
        if (status === google.maps.GeocoderStatus.OK) {
            if (results[0]) {
                if (results[0].formatted_address !== "Chicago, IL, USA") {
                    clearMapElements();

                    const result = results[0];
                    const formattedAddress = results[0].formatted_address.slice(0, -5); // remove ", USA" from returned address

                    $targets.input.val(formattedAddress);
                    addressMarker = setAddressMarker(map, result.geometry.location, formattedAddress);
                    addressMarker.formattedAddress = formattedAddress;
                    $targets.radius.removeAttr("hidden");
                    location = result.geometry.location;
                    radiusSearch(location);

                    // display schools within a radius of a google maps location
                    async function radiusSearch(location) {
                        // initiate a new school api call with location restricted by radius
                        const radiusMeters = Math.round($targets.radius.val() * metersPerMile);
                        const radiusAPIParameters = Object.assign({
                            latitude: location.lat().toString(),
                            longitude: location.lng().toString(),
                            radius: radiusMeters
                        }, mealAPIParameters);

                        const radiusDataFull = await getAPIValues(mealAPI, radiusAPIParameters);

                        // fill the global "radiusData" array with the radius search results and sort it by name
                        radiusData = [...radiusDataFull.features];
                        radiusData.sort((a, b) => a.properties.Name.localeCompare(b.properties.Name));

                        // draw the circle with the current location and radius
                        searchRadiusCircle = drawSearchRadiusCircle(map, location, radiusMeters, searchRadiusCircle);

                        updateFilters(radiusData);
                    }
                } else {
                    $targets.input.val("");
                    const myModal = new Modal(initModal("Enter a Chicago Address", $("<p>").html(`Please enter a valid Chicago address`)));
                    myModal.show();
                }
            }
        }
        // geocoder status not ok
        else {
            let $modal;
            if (request.location) { // if we did a lat/long search
                $modal = initModal("Geocoder failed", $("<p>").html(`Geocoder failed due to: ${status}`));
            } else { // if we did an address search
                $modal = initModal("Geocoder failed", $("<p>").html(`We could not find your address: ${status}`));
            }
            const myModal = new Modal($modal);
            myModal.show();
        }
    }

    // clear out elements displayed on the map
    function clearMapElements() {
        // close any open info windows
        infoWindow.close();

        // Reset the markers array if it already contains data
        markers.forEach(m => m.map = null);
        markers.length = 0;

        if (addressMarker) {
            addressMarker.map = null;
            addressMarker = null;
        }

        if (searchRadiusCircle) {
            searchRadiusCircle.setMap(null);
        }

        // clear the results list
        $targets.results.html("");
    }

    // LongPress on the map sets up mouse events and custom longPress event
    // thanks to stackoverflow - Leiko
    // https://stackoverflow.com/questions/6264853/using-longclick-taphold-with-google-maps-in-jquery-mobile
    function LongPress(length) {
        this.length_ = length;
        this.map_ = map;
        this.timeoutId_ = null;
        map.addListener("mousedown", e => this.onMouseDown_(e));
        map.addListener("mouseup", e => this.onMouseUp_(e));
        map.addListener("drag", e => this.onMapDrag_(e));
    }

    LongPress.prototype.onMouseUp_ = function (e) {
        clearTimeout(this.timeoutId_);
    };

    LongPress.prototype.onMouseDown_ = function (e) {
        clearTimeout(this.timeoutId_);
        const map = this.map_;
        this.timeoutId_ = setTimeout(() => google.maps.event.trigger(map, "longPress", e), this.length_);
    };

    LongPress.prototype.onMapDrag_ = function (e) {
        clearTimeout(this.timeoutId_);
    };

} catch (e) {
    console.error(e);
}